'use client';

/**
 * Overlay que aparece quando o navegador bloqueia o autoplay de áudio.
 *
 * Chrome e Safari bloqueiam <audio>.play() sem gesto do usuário.
 * LiveKit sinaliza via RoomEvent.AudioPlaybackStatusChanged + canPlaybackAudio.
 *
 * Qualquer clique/toque/tecla na página (e o botão do overlay) chama room.startAudio().
 */

import React from 'react';
import { RoomEvent } from 'livekit-client';
import { useRoomContext } from '@livekit/components-react';
import { shouldShowAudioUnlockOverlay, startRoomAudioIfBlocked } from '@/lib/audio-playback-unlock';

export function AudioAutoplayBanner() {
  const room = useRoomContext();
  const [canPlayback, setCanPlayback] = React.useState<boolean>(() =>
    room ? room.canPlaybackAudio : true,
  );

  const unlock = React.useCallback(async () => {
    if (!room) return;
    await startRoomAudioIfBlocked(room);
    setCanPlayback(room.canPlaybackAudio);
  }, [room]);

  React.useEffect(() => {
    if (!room) return;

    setCanPlayback(room.canPlaybackAudio);

    const handleChanged = () => {
      setCanPlayback(room.canPlaybackAudio);
    };

    room.on(RoomEvent.AudioPlaybackStatusChanged, handleChanged);
    return () => {
      room.off(RoomEvent.AudioPlaybackStatusChanged, handleChanged);
    };
  }, [room]);

  React.useEffect(() => {
    if (!room || canPlayback) return;

    const onGesture = () => {
      void unlock();
    };

    document.addEventListener('pointerdown', onGesture, { capture: true });
    document.addEventListener('keydown', onGesture, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', onGesture, { capture: true });
      document.removeEventListener('keydown', onGesture, { capture: true });
    };
  }, [room, canPlayback, unlock]);

  if (!shouldShowAudioUnlockOverlay(canPlayback)) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="evolumeet-audio-unlock-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 12000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(8, 10, 18, 0.78)',
        padding: 24,
      }}
    >
      <button
        type="button"
        onClick={() => {
          void unlock();
        }}
        aria-label="Clique para ativar o áudio da reunião"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          maxWidth: 440,
          width: '100%',
          background: '#f59e0b',
          color: '#111',
          border: 'none',
          borderRadius: 16,
          padding: '28px 24px',
          cursor: 'pointer',
          boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
        }}
      >
        <span aria-hidden="true" style={{ fontSize: 40, lineHeight: 1 }}>
          🔊
        </span>
        <span
          id="evolumeet-audio-unlock-title"
          style={{ fontSize: 20, fontWeight: 800, textAlign: 'center' }}
        >
          Clique aqui para ativar o áudio da reunião
        </span>
        <span style={{ fontSize: 14, fontWeight: 600, textAlign: 'center', opacity: 0.85 }}>
          O navegador bloqueou o som. Sem este clique você não ouve os outros participantes.
        </span>
      </button>
    </div>
  );
}
